import { CarPerf, CarState, Input, quantise } from './car';
import type { DriverSpec } from './drivers';
import { clamp, dcos, dsin } from './fmath';
import type { Path } from './path';
import { RngState, rngNext } from './rng';
import type { Track } from './track';
import { BRAKE_DECEL, DT } from './tuning';

/**
 * AI drivers (design.md B7): pure pursuit along a route, braking for the
 * next corners, traffic awareness with lane changes to overtake, per-lap
 * pace variation, occasional mistakes, shortcut choice, and an unstick
 * routine. Deterministic: all randomness comes from the AiState's own RNG.
 */

export interface AiState {
  rng: RngState;
  /** Index into track.routes for the current lap. */
  route: number;
  lane: number;
  laneTarget: number;
  lapPace: number;
  lastLap: number;
  lastSeg: number;
  overcook: number;
  passCooldown: number;
  /** Traffic index of the car we're overtaking, or -1. */
  passTarget: number;
  passTicks: number;
  /** A fluffed corner: off the throttle and wide for this many ticks. */
  fluffTicks: number;
  stuckTicks: number;
  reverseTicks: number;
  reverseSteer: number;
}

export function newAi(seed: number): AiState {
  return {
    rng: { seed: seed >>> 0 },
    route: 0,
    lane: 0,
    laneTarget: 0,
    lapPace: 1,
    lastLap: -2,
    lastSeg: -1,
    overcook: 1,
    passCooldown: 0,
    passTarget: -1,
    passTicks: 0,
    fluffTicks: 0,
    stuckTicks: 0,
    reverseTicks: 0,
    reverseSteer: 0,
  };
}

export interface Traffic {
  car: CarState;
  /** False for cars that can't be hit (crashed, finished, ghosted). */
  solid: boolean;
}

export interface AiContext {
  car: CarState;
  perf: CarPerf;
  track: Track;
  /** Laps completed (route and pace are re-rolled when this changes). */
  lapsDone: number;
  /** Field pace multiplier from difficulty (≈1.1 rookie … 1.42 legend). */
  basePace: number;
  traffic: readonly Traffic[];
  /** Cool-down after the flag: cruise, no racing. */
  cruise?: boolean;
}

/**
 * Corner speeds were tuned on Desert City (scale 24). Corners on a track
 * drawn at another scale have proportionally bigger/smaller radii, and
 * speed through a radius goes with its square root.
 */
export function cornerScaleFor(track: Track): number {
  return Math.sqrt(track.def.scale / 24);
}


/** Corner speed (px/s) for a direction change with cosine `dot`. */
function cornerSpeed(dot: number, vmax: number, pace: number, scale: number): number {
  const sharp = (1 - dot) / 2;
  const v = (330 - 360 * sharp) * scale;
  return clamp(v * pace, 70 * pace, vmax);
}

export function aiInput(ai: AiState, drv: DriverSpec, ctx: AiContext): Input {
  const { car, perf, track } = ctx;

  // New lap: pick a route and this lap's pace.
  if (ctx.lapsDone !== ai.lastLap) {
    ai.lastLap = ctx.lapsDone;
    const wantShortcut = track.routes.length > 1 && !ctx.cruise && rngNext(ai.rng) < drv.shortcut;
    ai.route = wantShortcut ? 1 : 0;
    ai.lapPace = 1 - (1 - drv.consistency) * rngNext(ai.rng) * 0.08;
  }
  const path: Path = track.routes[ai.route] ?? track.routes[0];
  const fx = dcos(car.h);
  const fy = dsin(car.h);
  const speed = Math.sqrt(car.vx * car.vx + car.vy * car.vy);
  const proj = path.project(car.x, car.y);

  // Mistakes: roll once per new segment for the corner at its end.
  if (proj.seg !== ai.lastSeg) {
    ai.lastSeg = proj.seg;
    ai.overcook = 1;
    if (!ctx.cruise) {
      const roll = rngNext(ai.rng);
      // Two kinds of error: overcooking it (risky: can end in the wall),
      // or fluffing it (safe, but loses time and maybe a place).
      if (roll < drv.mistakeRate) ai.overcook = 1.22;
      else if (roll < drv.mistakeRate * 3 + 0.01) ai.fluffTicks = 45 + Math.floor(rngNext(ai.rng) * 50);
    }
  }

  // ---- Traffic: who is ahead of us, and can we pass?
  const rx = -fy;
  const ry = fx;
  const lane = Math.min(22, track.halfRoad - 12);
  let followSpeed = Infinity;
  let blockedLat = 0;
  let blockedIdx = -1;
  if (!ctx.cruise) {
    for (let i = 0; i < ctx.traffic.length; i++) {
      const t = ctx.traffic[i];
      if (!t.solid || t.car === car) continue;
      const dx = t.car.x - car.x;
      const dy = t.car.y - car.y;
      const along = dx * fx + dy * fy;
      const lat = dx * rx + dy * ry;
      if (along <= 0 || along > 90 || lat > 24 || lat < -24) continue;
      const theirV = t.car.vx * fx + t.car.vy * fy;
      if (along < 70 && lat < 14 && lat > -14 && blockedIdx < 0) {
        blockedIdx = i;
        blockedLat = lat;
      }
      // Don't drive into the back of them: target their speed, less if close.
      // (While overtaking, the car we're passing only counts if still in line.)
      const inLine = i === ai.passTarget ? lat < 9 && lat > -9 : lat < 13 && lat > -13;
      const gapWanted = 24 + (1 - drv.aggression) * 14;
      const fsp = theirV + (along - gapWanted) * 2.2;
      if (inLine && fsp < followSpeed) followSpeed = fsp;
    }
  }
  if (ai.passCooldown > 0) ai.passCooldown--;
  if (ai.passTarget >= 0) {
    // Mid-overtake: hold the line until they're behind us (or we give up).
    const t = ctx.traffic[ai.passTarget];
    ai.passTicks++;
    const along = t ? (t.car.x - car.x) * fx + (t.car.y - car.y) * fy : -999;
    if (!t || !t.solid || along < -26 || along > 150 || ai.passTicks > 4 * 120 || path.shortcut) {
      ai.passTarget = -1;
      ai.laneTarget = 0;
      ai.passCooldown = 30;
    }
  } else if (blockedIdx >= 0 && !path.shortcut && ai.passCooldown === 0) {
    const narrow = track.halfRoad < 34 ? 0.45 : 1;
    if (rngNext(ai.rng) < (0.3 + drv.aggression * 0.6) * narrow) {
      // Go for the gap: the side they're not on, else the inside of the
      // next corner.
      let side: number;
      if (blockedLat > 4) side = -1;
      else if (blockedLat < -4) side = 1;
      else {
        const sg0 = path.segs[proj.seg];
        const sg1 = path.segs[(proj.seg + 1) % path.segs.length];
        side = sg0.ux * sg1.uy - sg0.uy * sg1.ux >= 0 ? 1 : -1;
      }
      ai.passTarget = blockedIdx;
      ai.passTicks = 0;
      ai.laneTarget = side * lane;
    }
    ai.passCooldown = 40;
  } else if (blockedIdx < 0 && ai.passCooldown === 0) {
    ai.laneTarget = 0;
  }
  if (path.shortcut) ai.laneTarget = 0;
  // No side-by-side into a tight corner on the outside: tuck in.
  let laneGoal = ai.laneTarget;
  {
    const sg0 = path.segs[proj.seg];
    const sg1 = path.segs[(proj.seg + 1) % path.segs.length];
    const toCorner = sg0.s0 + sg0.len - proj.s;
    const sharp = sg0.ux * sg1.ux + sg0.uy * sg1.uy < 0.6;
    const insideSign = sg0.ux * sg1.uy - sg0.uy * sg1.ux >= 0 ? 1 : -1;
    if (sharp && toCorner < 60 + speed * 0.5 && laneGoal * insideSign < 0) laneGoal = clamp(laneGoal, -6, 6);
  }
  const dl = clamp(laneGoal - ai.lane, -60 * DT, 60 * DT);
  ai.lane += dl;

  // ---- Steering: pure pursuit to a point ahead on the (laned) route.
  const look = 34 + speed * 0.22;
  const tp = path.pointAt(proj.s + look);
  const tx = tp.x - tp.uy * ai.lane;
  const ty = tp.y + tp.ux * ai.lane;
  const ddx = tx - car.x;
  const ddy = ty - car.y;
  const dlen = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
  const crossv = (fx * ddy - fy * ddx) / dlen;
  const dotv = (fx * ddx + fy * ddy) / dlen;
  let steer = dotv < 0 ? (crossv >= 0 ? 1 : -1) : clamp(crossv * 3.2, -1, 1);

  // ---- Speed: brake for the next corners within range.
  const pace = (ctx.cruise ? 0.75 : ctx.basePace * drv.pace * ai.lapPace) * ai.overcook;
  let throttle = 1;
  let brake = 0;
  const sg = path.segs[proj.seg];
  let dist = sg.s0 + sg.len - proj.s;
  const cs = cornerScaleFor(track);
  const window = 130 * cs;
  for (let k = 0; k < 5; k++) {
    const a = path.segs[(proj.seg + k) % path.segs.length];
    const b = path.segs[(proj.seg + k + 1) % path.segs.length];
    // Sharpness of this corner, including any bends right after it
    // (compound corners and chicanes read as one bigger corner).
    const mid = path.pointAt(a.s0 + a.len + window * 0.5);
    const after = path.pointAt(a.s0 + a.len + window);
    const dot = Math.min(a.ux * b.ux + a.uy * b.uy, a.ux * mid.ux + a.uy * mid.uy, a.ux * after.ux + a.uy * after.uy);
    const vc = cornerSpeed(dot, perf.vmax, pace, cs);
    const need = speed * speed - vc * vc;
    if (need > 0 && need > 2 * BRAKE_DECEL * 0.85 * Math.max(0, dist - 10)) {
      throttle = 0;
      brake = 1;
      break;
    }
    dist += b.len;
    if (dist > 1100) break;
  }
  if (dotv < 0.3 && speed > 120) throttle = 0;
  if (speed > followSpeed) {
    throttle = 0;
    if (speed > followSpeed + 20) brake = 1;
  }
  if (Math.abs(steer) > 0.98 && speed < 30) steer *= 0.6;
  if (ai.fluffTicks > 0) {
    ai.fluffTicks--;
    throttle = Math.min(throttle, 0.45);
  }

  // ---- Unstick: wedged against a wall or a parked car? Reverse out.
  if (ai.reverseTicks > 0) {
    ai.reverseTicks--;
    return quantise(ai.reverseSteer, 0, 1);
  }
  if (speed < 12 && throttle > 0 && !car.crashed) {
    ai.stuckTicks++;
    if (ai.stuckTicks > 75) {
      ai.stuckTicks = 0;
      ai.reverseTicks = 55;
      ai.reverseSteer = steer >= 0 ? -1 : 1;
    }
  } else {
    ai.stuckTicks = 0;
  }
  return quantise(steer, throttle, brake);
}
